// Determine if we're running in production
const isProduction = process.env.NODE_ENV === 'production';

// Get the base API URL based on environment
export const getApiUrl = () => {
  // For local development — use the dynamically assigned server port
  if (!isProduction) {
    return process.env.REACT_APP_API_URL || 'http://localhost:6001';
  }
  
  // For production and preview deployments on Vercel
  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`;
  }

  // Get the current hostname
  const hostname = window.location.hostname;

  // Check if we're on a Vercel preview URL
  if (hostname.match(/^leapyear-v2-[a-zA-Z0-9-]+-hankcoutures-projects\.vercel\.app$/)) {
    return `https://${hostname}`;
  }

  // Check if we're on the Vercel app domain
  if (hostname === 'leapyear-v2.vercel.app') {
    return 'https://leapyear-v2.vercel.app';
  }

  // Check if we're on takeleapyear.com
  if (hostname === 'www.takeleapyear.com') {
    return 'https://www.takeleapyear.com';
  }

  // Default to the official domain
  return 'https://www.leapyear.us';
};

// Helper function to build API endpoints
export const endpoints = {
  submitApplication: () => `/api/applications/submit`,
  subscribe: () => `/api/applications/subscribe`,
  submitInvestor: () => `/api/applications/investor`,
  testEmail: () => `/api/email/test`,
  csrfToken: () => `/api/csrf-token`,
  finalist: () => `/api/public/finalist`,
  reference: () => `/api/public/reference`,
  auth: {
    gmail: () => `/api/public/auth-gmail`,
    callback: () => `/api/public/auth-callback`
  }
};

// Track retry attempts
const retryAttempts = new Map();

// Helper function for making API requests
export const fetchApi = async (endpoint, options = {}) => {
  const apiUrl = getApiUrl();
  const { params, ...restOptions } = options;
  
  const cleanEndpoint = endpoint.replace(/^https?:\/\/[^/]+/, '').replace(/^\/+/, '');
  const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
  const fullUrl = `${apiUrl}/${cleanEndpoint}${queryString}`;

  // For non-GET requests, ensure we have a CSRF token
  let csrfToken = null;
  if (options.method && options.method !== 'GET') {
    try {
      if (!isProduction) console.log(`✓ Fetching CSRF token for ${options.method} ${cleanEndpoint}`);
      const tokenResponse = await fetch(`${apiUrl}/api/csrf-token`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Origin': window.location.origin
        }
      });

      if (!tokenResponse.ok) {
        if (!isProduction) console.log(`✗ Failed to fetch CSRF token for ${options.method} ${cleanEndpoint}`);
        throw new Error(`Failed to fetch CSRF token: ${tokenResponse.statusText}`);
      }

      // Use the token straight from the response body — no reliance on
      // cookie-write timing (the old approach parsed document.cookie after a
      // 100ms sleep, which raced browser cookie handling)
      const tokenBody = await tokenResponse.json();
      csrfToken = tokenBody.csrfToken || null;
      if (!isProduction) console.log(`✓ CSRF token received for ${options.method} ${cleanEndpoint}`);
    } catch (error) {
      if (!isProduction) console.log(`✗ CSRF setup failed for ${options.method} ${cleanEndpoint}: ${error.message}`);
      throw error;
    }
  }

  // Fallback: read the token from the cookie (set on every API response)
  if (!csrfToken) {
    const cookieToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('XSRF-TOKEN'))
      ?.split('=')[1];
    csrfToken = cookieToken ? decodeURIComponent(cookieToken) : null;
  }

  if (!csrfToken && options.method && options.method !== 'GET') {
    if (!isProduction) console.log(`✗ No CSRF token found for ${options.method} ${cleanEndpoint}`);
    throw new Error('No CSRF token available');
  }

  // Add headers including CSRF token if available
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'Origin': window.location.origin,
    ...(csrfToken && { 'X-CSRF-Token': csrfToken }),
    ...options.headers,
  };

  try {
    const response = await fetch(fullUrl, {
      ...restOptions,
      credentials: 'include',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      if (response.status === 403 && error.error === 'Invalid CSRF token') {
        const currentAttempts = retryAttempts.get(fullUrl) || 0;
        if (currentAttempts >= 3) {
          retryAttempts.delete(fullUrl);
          if (!isProduction) console.log(`✗ Max CSRF retries reached for ${options.method} ${cleanEndpoint}`);
          throw new Error('Maximum retry attempts reached for CSRF token');
        }
        
        retryAttempts.set(fullUrl, currentAttempts + 1);
        if (!isProduction) console.log(`⟳ Retrying with new CSRF token for ${options.method} ${cleanEndpoint} (${currentAttempts + 1}/3)`);
        
        document.cookie = 'XSRF-TOKEN=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        
        return fetchApi(endpoint, options);
      }
      if (!isProduction) console.log(`✗ ${options.method} ${cleanEndpoint} failed: ${error.message || response.statusText}`);
      // Carry the HTTP status and server payload so callers can react to
      // specific failures (409 duplicate = the earlier attempt succeeded,
      // 429 rate limited, 413 too large, ...)
      const apiError = new Error(error.message || error.error || `API request failed: ${response.status} ${response.statusText}`);
      apiError.status = response.status;
      apiError.body = error;
      throw apiError;
    }

    if (!isProduction) console.log(`✓ ${options.method} ${cleanEndpoint} succeeded`);
    retryAttempts.delete(fullUrl);
    return response.json();
  } catch (error) {
    if (!isProduction) console.log(`✗ ${options.method} ${cleanEndpoint} error: ${error.message}`);
    throw error;
  }
};

// Report a client-side form failure to the server's error log. Covers
// failures that never produce a server-side form POST (CSRF token fetch
// failed, cookies blocked, network down). Fire-and-forget: never throws,
// never blocks the UI.
export const logClientError = (form, error, extra = {}) => {
  try {
    const payload = JSON.stringify({
      form,
      message: String(error?.message || error || 'unknown').slice(0, 500),
      status: typeof error?.status === 'number' ? error.status : null,
      stage: extra.stage || 'client',
      page: window.location.pathname,
    });
    fetch(`${getApiUrl()}/api/public/client-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    // Never let error reporting cause an error
  }
};

export const getFrontendURL = () => {
  if (typeof window === 'undefined') return process.env.FRONTEND_URL_PUBLIC;
  
  const hostname = window.location.hostname;
  if (hostname.includes('vercel.app')) {
    return process.env.FRONTEND_URL_VERCEL;
  }
  return process.env.FRONTEND_URL_PUBLIC;
}; 